// ============================================================================
// ClientCalendarPage — route /me/calendar (guarded). The client's own colour-
// coded calendar: a month grid merging their PT sessions (coral), trainer
// homework (violet) and self-logged exercise (lime), from
// GET /api/client/me/calendar-entries. Tapping a day lists that day's entries;
// tapping an entry opens its detail. Shares the bilingual CalendarLegend +
// ENTRY_STYLE palette with the trainer calendar so both sides read the same
// colour language. Mobile-first, Pulse, EN + 繁中, reduced-motion aware.
// ============================================================================
import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, EmptyState } from "../../components";
import {
  IconChevronLeft,
  IconChevronRight,
  IconMapPin,
} from "../../components/icons";
import {
  addMonths,
  formatDayLong,
  formatTime,
  isSameDay,
  localDayKey,
  monthGrid,
  monthTitle,
  weekdayLabels,
} from "../../lib/date";
import { CalendarLegend } from "../../dashboard/CalendarLegend";
import { ENTRY_STYLE } from "../../dashboard/sessionStyle";
import { DaySummary, type DaySummaryItem } from "../../dashboard/DaySummary";
import {
  activityToVM,
  clientMealMarkerToVM,
  toLocalDateTime,
  weeklyGoalBannerToVM,
  type ActivityVM,
  type MealMarkerVM,
  type SessionVM,
} from "../../dashboard/viewModel";
import { fmtDate } from "./portalLib";
import { ActivityDetailSheet } from "../../dashboard/ActivityDetailSheet";
import { MealMarkerSheet } from "../../dashboard/MealMarkerSheet";
import { useClientCalendarEntries } from "../hooks";
import type { ClientCalendarEntry } from "../../api";
import { cn } from "../../lib/cn";

/** A merged client calendar entry (session, activity OR meal), time-keyed locally. */
type Entry =
  | { kind: "session"; data: SessionVM; startsAt: string }
  | { kind: "activity"; data: ActivityVM; startsAt: string }
  | { kind: "meal"; data: MealMarkerVM; startsAt: string };

/** Fixed "today" for the render pass (stable cursor/selection). */
const TODAY = (() => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
})();

/** Map a client session row (kind='pt_session') to a minimal SessionVM. */
function sessionEntryToVM(s: Extract<ClientCalendarEntry, { kind: "pt_session" }>): SessionVM {
  return {
    id: s.id,
    clientId: s.client_id,
    clientName: "",
    status: s.status === "completed" ? "completed" : "planned",
    startsAt: toLocalDateTime(s.performed_at ?? s.scheduled_at),
    durationMin: s.duration_min ?? 0,
    type: "mixed",
    location: s.location_text ?? undefined,
    notes: s.notes ?? undefined,
    attendance: "scheduled",
  };
}

export function ClientCalendarPage() {
  const { t } = useTranslation("activity");
  const reduce = useReducedMotion();
  const query = useClientCalendarEntries();

  const [cursor, setCursor] = useState<Date>(() => TODAY);
  const [selected, setSelected] = useState<Date>(() => TODAY);
  const [openActivity, setOpenActivity] = useState<ActivityVM | null>(null);
  const [openMeal, setOpenMeal] = useState<MealMarkerVM | null>(null);

  // Normalize the union into time-sorted, local-day-keyed entries. Week-level
  // weekly_goal banners are split out into their own Set (keyed on the Monday
  // week_start) so they can render as a slim lime bar over the matching week row
  // rather than as a single-day chip.
  const entries = useMemo<Entry[]>(() => {
    const rows = query.data ?? [];
    const out: Entry[] = [];
    for (const r of rows) {
      if (r.kind === "pt_session") {
        const vm = sessionEntryToVM(r);
        if (vm.startsAt) out.push({ kind: "session", data: vm, startsAt: vm.startsAt });
      } else if (r.kind === "meal") {
        const vm = clientMealMarkerToVM(r);
        if (vm.startsAt) out.push({ kind: "meal", data: vm, startsAt: vm.startsAt });
      } else if (r.kind === "weekly_goal") {
        // handled separately (week-level banner), not a day entry
      } else {
        const vm = activityToVM(r);
        if (vm.startsAt) out.push({ kind: "activity", data: vm, startsAt: vm.startsAt });
      }
    }
    return out.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  }, [query.data]);

  // The set of Monday week_start days ('YYYY-MM-DD') that have a coach goal —
  // drives the lime week-level banner over each matching week row.
  const goalWeeks = useMemo<Set<string>>(() => {
    const rows = query.data ?? [];
    const set = new Set<string>();
    for (const r of rows) {
      if (r.kind === "weekly_goal") {
        const vm = weeklyGoalBannerToVM(r);
        if (vm.weekStart) set.add(vm.weekStart);
      }
    }
    return set;
  }, [query.data]);

  const entriesOnDay = (day: Date): Entry[] => {
    const key = localDayKey(day);
    return entries.filter((e) => e.startsAt.startsWith(key));
  };

  const summaryItems = (dayEntries: Entry[]): DaySummaryItem[] =>
    dayEntries.map((e) => {
      if (e.kind === "session")
        // sessionEntryToVM hardcodes type:"mixed"; use the i18n label directly.
        return { provenance: "pt_session" as const, label: t("calendar.session") };
      if (e.kind === "meal")
        return { provenance: "meal" as const, label: t("calendar.mealCount", { count: e.data.count ?? 1 }) };
      return { provenance: e.data.kind, label: e.data.title || e.data.type || t("calendar.activity") };
    });

  const days = useMemo(() => monthGrid(cursor), [cursor]);
  const labels = useMemo(() => weekdayLabels(), []);
  const month = cursor.getMonth();
  const selectedEntries = entriesOnDay(selected);

  const fade = {
    initial: reduce ? false : { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.35, ease: [0.22, 0.61, 0.36, 1] as const },
  };

  return (
    <div className="space-y-5">
      <motion.div {...fade}>
        <h1 className="font-display text-2xl font-extrabold tracking-tightest text-body">
          {t("calendar.title")}
        </h1>
        <p className="mt-1 text-body-soft">{t("calendar.subtitle")}</p>
      </motion.div>

      {query.isError ? (
        <EmptyState
          title={t("calendar.loadError")}
          action={
            <Button size="sm" onClick={() => void query.refetch()}>
              {t("calendar.retry")}
            </Button>
          }
        />
      ) : (
        <Card padded={false} className="overflow-hidden">
          {/* Header: month title + nav */}
          <div className="flex items-center justify-between border-b border-line p-4">
            <h2 className="font-display text-lg font-extrabold tracking-tightest">
              {monthTitle(cursor)}
            </h2>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setCursor((c) => addMonths(c, -1))}
                aria-label={t("calendar.title")}
                className="grid h-9 w-9 place-items-center rounded-pill bg-surface text-body-soft ring-1 ring-line transition-colors hover:bg-surface-sunken hover:text-body"
              >
                <IconChevronLeft />
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCursor(TODAY);
                  setSelected(TODAY);
                }}
              >
                {t("calendar.today")}
              </Button>
              <button
                type="button"
                onClick={() => setCursor((c) => addMonths(c, 1))}
                aria-label={t("calendar.title")}
                className="grid h-9 w-9 place-items-center rounded-pill bg-surface text-body-soft ring-1 ring-line transition-colors hover:bg-surface-sunken hover:text-body"
              >
                <IconChevronRight />
              </button>
            </div>
          </div>

          {/* Legend */}
          <CalendarLegend className="px-4 pt-3" />

          {/* Month grid */}
          <div className="p-3">
            <div className="mb-1.5 grid grid-cols-7 gap-1">
              {labels.map((w, i) => (
                <div
                  key={i}
                  className="px-1 text-center text-[0.62rem] font-bold uppercase tracking-kicker text-body-faint"
                >
                  {/* CJK weekday labels (e.g. zh "week-one") read by their last
                      glyph; Latin ones (Mon) by their first letter (M). */}
                  {/[一-鿿]/.test(w) ? w.slice(-1) : w.slice(0, 1)}
                </div>
              ))}
            </div>
            {/* Six week rows; each carries a slim lime weekly-goal banner over
                the row when that Monday (the row's first cell) has a coach goal. */}
            <div className="space-y-1">
              {Array.from({ length: 6 }, (_, w) => {
                const rowDays = days.slice(w * 7, w * 7 + 7);
                const monday = rowDays[0];
                const weekKey = monday ? localDayKey(monday) : "";
                const hasGoal = weekKey !== "" && goalWeeks.has(weekKey);
                return (
                  <div key={`wk-${weekKey}-${w}`}>
                    {hasGoal && (
                      <div
                        className="mb-1 flex items-center gap-1.5 rounded-pill bg-grad-lime px-2.5 py-1 text-white shadow-sm"
                        aria-label={t("weeklyGoals:banner.aria", { date: fmtDate(weekKey) })}
                      >
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-white/90" aria-hidden />
                        <span className="truncate font-display text-[0.68rem] font-bold tracking-tight">
                          {t("weeklyGoals:banner.title")}
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-7 gap-1">
                      {rowDays.map((day) => {
                        const items = entriesOnDay(day);
                        const inMonth = day.getMonth() === month;
                        const isToday = isSameDay(day, TODAY);
                        const isSelected = isSameDay(day, selected);
                        return (
                          <button
                            key={day.toISOString()}
                            type="button"
                            onClick={() => setSelected(day)}
                            aria-pressed={isSelected}
                            aria-label={formatDayLong(day)}
                            className={cn(
                              "flex min-h-[4.5rem] flex-col items-center rounded-md p-1 transition-all duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt",
                              inMonth ? "bg-surface ring-1 ring-line" : "bg-surface-sunken/40 ring-1 ring-line-soft",
                              isSelected ? "ring-2 ring-coral shadow-volt" : "hover:ring-line-strong",
                            )}
                          >
                            <span
                              className={cn(
                                "grid h-6 min-w-[1.5rem] place-items-center rounded-pill px-1 text-xs font-bold tabular-nums",
                                isToday
                                  ? "bg-grad-coral text-white shadow-volt"
                                  : inMonth
                                    ? "text-body"
                                    : "text-body-faint",
                              )}
                            >
                              {day.getDate()}
                            </span>
                            <DaySummary
                              items={summaryItems(items)}
                              max={1}
                              moreLabel={(n) => t("calendar.moreCount", { count: n })}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selected-day list */}
          <div className="border-t border-line bg-surface-soft p-4">
            <h3 className="mb-3 font-display text-base font-extrabold tracking-tight">
              {isSameDay(selected, TODAY) ? `${t("calendar.today")} · ` : ""}
              {formatDayLong(selected)}
            </h3>
            {selectedEntries.length === 0 ? (
              <p className="rounded-md bg-surface px-4 py-6 text-center text-sm text-body-muted ring-1 ring-line">
                {t("calendar.empty")}
              </p>
            ) : (
              <ul className="space-y-2">
                {selectedEntries.map((e) =>
                  e.kind === "session" ? (
                    <SessionRow key={`s${e.data.id}`} session={e.data} />
                  ) : e.kind === "meal" ? (
                    <MealRow
                      key={`m${e.data.id}`}
                      marker={e.data}
                      onPick={setOpenMeal}
                    />
                  ) : (
                    <ActivityRow
                      key={`a${e.data.id}`}
                      activity={e.data}
                      onPick={setOpenActivity}
                    />
                  ),
                )}
              </ul>
            )}
          </div>
        </Card>
      )}

      <ActivityDetailSheet
        activity={openActivity}
        onClose={() => setOpenActivity(null)}
      />

      <MealMarkerSheet
        marker={openMeal}
        onClose={() => setOpenMeal(null)}
      />
    </div>
  );
}

function SessionRow({ session }: { session: SessionVM }) {
  const { t } = useTranslation("activity");
  const st = ENTRY_STYLE.pt_session;
  return (
    <li className="flex items-center gap-3 rounded-md bg-surface p-3 ring-1 ring-line">
      <span className={cn("h-10 w-1.5 shrink-0 rounded-pill", st.grad)} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-display text-sm font-bold text-body tabular-nums">
            {session.startsAt ? formatTime(session.startsAt) : ""}
          </span>
          <Badge tone={st.tone} className="shrink-0">
            {t("provenance.pt_session")}
          </Badge>
        </div>
        {session.location && (
          <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-body-soft">
            <IconMapPin width={13} height={13} />
            <span className="truncate">{session.location}</span>
          </div>
        )}
      </div>
      <Badge tone={session.status === "completed" ? "success" : "neutral"} dot className="shrink-0">
        {session.status === "completed"
          ? t("detail.statusCompleted")
          : t("detail.statusPlanned")}
      </Badge>
    </li>
  );
}

function ActivityRow({
  activity,
  onPick,
}: {
  activity: ActivityVM;
  onPick: (a: ActivityVM) => void;
}) {
  const { t } = useTranslation("activity");
  const st = ENTRY_STYLE[activity.kind];
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(activity)}
        className="flex w-full items-center gap-3 rounded-md bg-surface p-3 text-left ring-1 ring-line transition-all duration-fast hover:-translate-y-0.5 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
      >
        <span className={cn("h-10 w-1.5 shrink-0 rounded-pill", st.grad)} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-display text-sm font-bold text-body">
              {activity.title || activity.type || t("type.fallback")}
            </span>
            <Badge tone={st.tone} className="shrink-0">
              {t(`provenance.${activity.kind}`)}
            </Badge>
          </div>
          <div className="mt-0.5 text-xs text-body-soft tabular-nums">
            {activity.startsAt ? formatTime(activity.startsAt) : ""}
            {activity.type ? ` · ${activity.type}` : ""}
          </div>
        </div>
        <Badge tone={activity.status === "completed" ? "success" : "neutral"} dot className="shrink-0">
          {activity.status === "completed"
            ? t("detail.statusCompleted")
            : t("detail.statusPlanned")}
        </Badge>
      </button>
    </li>
  );
}

/** A selected-day row for the per-day meal marker (amber, total kcal + count). */
function MealRow({
  marker,
  onPick,
}: {
  marker: MealMarkerVM;
  onPick: (m: MealMarkerVM) => void;
}) {
  const { t } = useTranslation("nutrition");
  const st = ENTRY_STYLE.meal;
  const kcal = Math.round(marker.kcal);
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(marker)}
        className="flex w-full items-center gap-3 rounded-md bg-surface p-3 text-left ring-1 ring-line transition-all duration-fast hover:-translate-y-0.5 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
      >
        <span className={cn("h-10 w-1.5 shrink-0 rounded-pill", st.grad)} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-display text-sm font-bold text-body">
              {t("marker.title")}
            </span>
            <Badge tone={st.tone} className="shrink-0">
              {marker.count > 0
                ? t("marker.mealCount", { count: marker.count })
                : t("marker.title")}
            </Badge>
          </div>
        </div>
        <span className="shrink-0 font-display text-sm font-extrabold tabular-nums text-body">
          {kcal.toLocaleString()}{" "}
          <span className="text-xs font-bold text-body-soft">
            {t("macro.kcalUnit")}
          </span>
        </span>
      </button>
    </li>
  );
}
